import { useCallback, useMemo } from 'react';
import { PlanStatuses } from '@common-fe/common-fe';

import { usePlanTerminationModalStore, useViewPlanTerminationModalStore } from '../../store';
import { useGetPlanTerminationPropertiesQuery } from '../queries/useGetPlanTerminationProperties.query';

import { usePlanTerminationCoverageDates } from './usePlanTerminationCoverageDates';

export const useViewPlanTermination = () => {
  const planId = usePlanTerminationModalStore((state) => state.planId);
  const planName = usePlanTerminationModalStore((state) => state.planName);
  const terminationStatus = usePlanTerminationModalStore((state) => state.terminationStatus);
  const isHsaAccount = usePlanTerminationModalStore((state) => state.isHsaAccount);
  const handleChangeVisibility = useViewPlanTerminationModalStore((state) => state.handleChangeVisibility);
  const handleSetPlanId = usePlanTerminationModalStore((state) => state.handleSetPlanId);
  const handleSetPlanName = usePlanTerminationModalStore((state) => state.handleSetPlanName);
  const handleSetTerminationStatus = usePlanTerminationModalStore((state) => state.handleSetTerminationStatus);
  const handleSetIsHsaAccount = usePlanTerminationModalStore((state) => state.handleSetIsHsaAccount);

  const isPendingTermination = useMemo(() => {
    return terminationStatus
    && (terminationStatus === PlanStatuses.PENDING_TERMINATION);
  }, [terminationStatus]);

  const isTerminationDataCanBeLoaded = useMemo(() => {
    return isPendingTermination
    || terminationStatus === PlanStatuses.TERMINATED
    || terminationStatus === PlanStatuses.TERMINATION_IN_PROGRESS;
  }, [isPendingTermination, terminationStatus]);

  const { formattedData, isLoading: terminationDataLoading } = useGetPlanTerminationPropertiesQuery(isTerminationDataCanBeLoaded ? planId || '' : undefined);

  const { coverageData } = usePlanTerminationCoverageDates({
    planId,
    planName,
    endOfCoverage: formattedData?.coverageEndDate,
    lastDayToSubmitClaim: formattedData?.claimLastDate,
    viewMode: true,
  });

  const currentTerminationDate = useMemo(() => {
    return formattedData?.terminationDate || '';
  }, [formattedData]);

  const handleOpenViewPlanTerminationModal = useCallback(
    (
      planId?: string,
      planName?: string,
      terminationStatus?: PlanStatuses,
      isHsaAccount?: boolean
    ) => {
    if (planId) handleSetPlanId(planId);
    if (planName) handleSetPlanName(planName);
    if (terminationStatus) handleSetTerminationStatus(terminationStatus);
    handleSetIsHsaAccount(isHsaAccount);
    handleChangeVisibility(true);
  }, [
    handleChangeVisibility,
    handleSetPlanId,
    handleSetPlanName,
    handleSetTerminationStatus,
    handleSetIsHsaAccount,
  ]);

  const handleOpenViewPlanTerminationModalWithDefaults = useCallback(() => {
    handleChangeVisibility(true);
  }, [handleChangeVisibility]);
  

  return {
    isPendingTermination,
    planName,
    currentTerminationDate,
    coverageData,
    isHsaAccount,
    formattedData,
    terminationDataLoading,
    handleOpenViewPlanTerminationModal,
    handleOpenViewPlanTerminationModalWithDefaults,
  };
};
